import { type FormEvent, type ReactElement, type ReactNode, useState } from 'react';
import { Button, Input } from '@/shared/ui';
// 같은 슬라이스 안에서는 배럴(@/entities/product)을 거치면 자기 참조 순환이 된다 — 상대 경로로 가져온다.
import { useProductsListQuery } from '../api/use-products-list-query';

interface PublishedProductPickerProps {
  readonly title: string;
  /** 제목 아래 안내 한 줄 — 추천·기획전이 다른 문구를 쓴다. */
  readonly description: string;
  /** 이미 묶인 상품에 붙는 라벨 — "추천 중"·"묶임". */
  readonly linkedLabel: string;
  /** 이미 묶여 있어 추가 버튼을 끌 상품 id 들. */
  readonly linkedProductIds: readonly string[];
  /** true 면 모든 추가 버튼을 끈다 — 자리가 다 찼거나(예: 추천 8개) 지금은 묶을 수 없는 상태일 때. */
  readonly isAddDisabled: boolean;
  readonly isPending: boolean;
  readonly errorMessage: string | null;
  readonly onAdd: (productId: string) => void;
  readonly onClose: () => void;
  /** 검색 결과 아래에 덧붙일 내용 — 기획전 상품 관리의 묶인 상품 목록. */
  readonly children?: ReactNode;
}

/**
 * 공개 상품 선택 모달 — 공개 상품을 이름으로 찾아 목록에 넣는다.
 * 여러 개를 이어서 넣을 수 있게 추가 뒤에도 닫지 않는다. 이미 묶인 상품은 받은 라벨로 표시한다.
 */
export const PublishedProductPicker = ({
  title,
  description,
  linkedLabel,
  linkedProductIds,
  isAddDisabled,
  isPending,
  errorMessage,
  onAdd,
  onClose,
  children,
}: PublishedProductPickerProps): ReactElement => {
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const productsQuery = useProductsListQuery({ page: 1, status: 'PUBLISHED', search });
  const products = productsQuery.data?.items ?? [];

  const handleSearch = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setSearch(searchDraft.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-[80dvh] w-full max-w-md flex-col rounded-lg border bg-background p-5"
      >
        <p className="font-medium">{title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>

        {errorMessage !== null ? (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {errorMessage}
          </p>
        ) : null}

        <form onSubmit={handleSearch} className="mt-4 flex gap-2">
          <Input
            aria-label="상품명 검색"
            placeholder="상품명"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            autoFocus
          />
          <Button type="submit" size="sm" variant="outline">
            검색
          </Button>
        </form>

        <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
          {productsQuery.isPending ? (
            <p className="text-sm text-muted-foreground">불러오는 중…</p>
          ) : productsQuery.isError ? (
            <p role="alert" className="text-sm text-destructive">
              상품을 불러오지 못했습니다.
            </p>
          ) : products.length === 0 ? (
            <p className="text-sm text-muted-foreground">검색 결과가 없습니다.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {products.map((product) => {
                const isLinked = linkedProductIds.includes(product.id);

                return (
                  <li key={product.id} className="flex items-center gap-3">
                    {product.thumbnail !== null ? (
                      <img
                        src={product.thumbnail.url}
                        alt=""
                        width={32}
                        height={32}
                        className="size-8 rounded object-cover"
                      />
                    ) : (
                      <span aria-hidden className="size-8 rounded bg-muted" />
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm">{product.name}</span>
                    {isLinked ? (
                      <span className="text-xs text-muted-foreground">{linkedLabel}</span>
                    ) : null}
                    <Button
                      size="sm"
                      disabled={isAddDisabled || isPending || isLinked}
                      onClick={() => onAdd(product.id)}
                      aria-label={`${product.name} 추가`}
                    >
                      추가
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}

          {children}
        </div>

        <div className="mt-4 flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            닫기
          </Button>
        </div>
      </div>
    </div>
  );
};
